import * as uuid from "uuid";
import { getStorgeProvider } from "./storage";
const PERSISTED_KEY = "ca-launchdarkly-context-key";
/**
 * Retrieves a generated/cached UUID in browser environment and generated UUID
 * in node environment
 *
 * @returns A randomly generated anonymous key
 */
export function getAnonymousKey(storageProvider = getStorgeProvider()) {
    const cachedKey = storageProvider?.get(PERSISTED_KEY);
    // Browser environment only
    if (cachedKey && uuid.validate(cachedKey)) {
        return cachedKey;
    }
    const generatedKey = uuid.v1();
    try {
        storageProvider?.set(PERSISTED_KEY, generatedKey); // Browser environment only
    }
    catch (error) { }
    return generatedKey;
}
/**
 * Transforms CA's evaluation context to LaunchDarkly's multi kind context
 *
 * @param context CA's evaluation context
 * @returns LaunchDarkly's multi kind context
 */
export const transformContext = (context = {}) => {
    const { userID, realUserID, surveyID, accountID } = context;
    const transformed = {
        kind: "multi",
        ...(userID ? { user: { key: userID } } : {}),
        ...(userID && realUserID ? { user: { key: userID, realUserID } } : {}),
        ...(!userID && realUserID ? { user: { key: realUserID, realUserID } } : {}),
        ...(surveyID ? { survey: { key: surveyID } } : {}),
        ...(accountID ? { account: { key: accountID } } : {}),
        // If no context property is provided, we assume anonymous user:
        ...(!userID && !realUserID && !surveyID && !accountID
            ? { user: { key: getAnonymousKey(), anonymous: true } }
            : {}),
    };
    // If `type` prop is set, we assume a legacy context (backward compatibility).
    // @FIXME: Remove the following in next major release once we have migrated all our flags to use the new context
    if (context.type === "user") {
        return applyLegacyUserContext(transformed, userID, realUserID, accountID);
    }
    if (context.type === "survey" && surveyID) {
        return applyLegacySurveyContext(transformed, surveyID, accountID);
    }
    return transformed;
};
/**
 * Backward compatibility helper function to apply legacy user context on top of
 * transformed context
 */
function applyLegacyUserContext(transformed, userID, realUserID, accountID) {
    const finalUserID = userID ?? getAnonymousKey();
    return {
        ...transformed,
        user: {
            key: finalUserID,
            anonymous: userID ? false : true,
            ...(finalUserID ? { userID: finalUserID } : {}),
            ...(accountID ? { accountID } : {}),
            ...(realUserID ? { realUserID } : {}),
        },
    };
}
/**
 * Backward compatibility helper function to apply legacy survey context on top of
 * transformed context
 */
function applyLegacySurveyContext(transformed, surveyID, accountID) {
    return {
        ...transformed,
        user: {
            key: surveyID,
            anonymous: false,
            ...(surveyID ? { surveyID } : {}),
            ...(accountID ? { accountID } : {}),
        },
    };
}
